/**
 * Staff admin page.
 *
 * Layout:
 *   PageHeader → HeroPanel (total / by-role / MFA %)
 *              → MFA-enforcement banner (owner-gated, UI-only until backend)
 *              → Role audit panel (per-role MFA stats)
 *              → Staff avatar grid
 *              → Invite drawer (replaces inline form)
 */
import { useEffect, useMemo, useState, type JSX } from 'react';
import {
  Users, RefreshCw, UserPlus, ShieldCheck, ShieldAlert,
  PowerOff, Power, ScanLine, Crown,
} from 'lucide-react';
import {
  AdminApiClient, 
  type StaffRow, type StaffRole,
} from '../api';
import { userMessageFor, failureFrom, type Failure } from '../lib/api-state';
import { usePermissions } from '../lib/permissions';
import { useSession } from '../session';
import { AccessNotice } from '../components/ui/AccessNotice';
import { PageHeader } from '../components/ui/PageHeader';
import { HeroPanel, HeroKpi } from '../components/ui/HeroPanel';
import { Button } from '../components/ui/Button';
import { StatusChip, type Band } from '../components/ui/StatusChip';
import { FetchState } from '../components/ui/FetchState';
import { Drawer } from '../components/ui/Drawer';

const ROLES: StaffRole[] = ['owner', 'admin', 'support', 'readonly'];

const ROLE_TONES: Record<StaffRole, string> = {
  owner:    '#fbbf24',
  admin:    '#a78bfa',
  support:  '#22d3ee',
  readonly: '#94a3b8',
};

function bandForRole(role: StaffRole): Band {
  switch (role) {
    case 'owner':    return 'warning';
    case 'admin':    return 'degraded';
    case 'support':  return 'healthy';
    case 'readonly': return 'neutral';
  }
}

export function StaffPage(): JSX.Element {
  const { state } = useSession();
  const [rows, setRows] = useState<StaffRow[] | null>(null);
  const [includeDisabled, setIncludeDisabled] = useState(false);
  const [err, setErr] = useState<Failure | null>(null);
  const [version, setVersion] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);

  const perms = usePermissions();
  const canManage = perms.canOwner;
  // GET /internal/staff is owner/admin only on the server. Support and
  // Read-only accounts get an explanation instead of a failing request.
  const canRead = perms.canReadAdminData;

  useEffect(() => {
    if (!canRead) { setRows(null); return; }
    setRefreshing(true);
    const api = new AdminApiClient({ baseUrl: '', getToken: () => state.token });
    api.listStaff(includeDisabled)
      .then((r) => { setRows(r.staff); setErr(null); })
      .catch((e) => { setRows(null); setErr(failureFrom(e, 'the staff list')); })
      .finally(() => setRefreshing(false));
  }, [state.token, includeDisabled, version, canRead]);

  const onRefresh = (): void => setVersion((v) => v + 1);

  const errMsg = (e: unknown): string =>
    userMessageFor(e);

  const onDisable = async (id: string): Promise<void> => {
    if (!confirm('Disable this staff user? They can be re-enabled later.')) return;
    const api = new AdminApiClient({ baseUrl: '', getToken: () => state.token });
    try { await api.disableStaff(id); onRefresh(); }
    catch (e) { alert(errMsg(e)); }
  };
  const onEnable = async (id: string): Promise<void> => {
    const api = new AdminApiClient({ baseUrl: '', getToken: () => state.token });
    try { await api.enableStaff(id); onRefresh(); }
    catch (e) { alert(errMsg(e)); }
  };
  const onChangeRole = async (id: string, role: StaffRole): Promise<void> => {
    const api = new AdminApiClient({ baseUrl: '', getToken: () => state.token });
    const who = rows?.find((r) => r.id === id)?.email ?? 'this user';
    if (!confirm(`Change ${who} to the ${role} role? This changes what they can do immediately.`)) return;
    try { await api.updateStaff(id, { role }); onRefresh(); }
    catch (e) { alert(errMsg(e)); }
  };

  const stats = useMemo(() => {
    const live = (rows ?? []).filter((r) => !r.disabled_at);
    const byRole: Record<StaffRole, number> = { owner: 0, admin: 0, support: 0, readonly: 0 };
    let mfa = 0;
    for (const r of live) {
      byRole[r.role] = (byRole[r.role] ?? 0) + 1;
      if (r.totp_enabled) mfa += 1;
    }
    const mfaPct: number | null = live.length > 0 ? Math.round((mfa / live.length) * 100) : null;
    return {
      total: rows?.length ?? 0,
      live: live.length,
      disabled: (rows ?? []).filter((r) => r.disabled_at).length,
      byRole,
      mfa,
      mfaPct,
    };
  }, [rows]);

  if (!canRead) {
    return (
      <div className="page">
        <PageHeader
          title="Staff"
          subtitle="Admin dashboard access."
          icon={Users}
          accent="#a78bfa"
        />
        <FetchState
          error={{
            kind: 'no_access', retryable: false,
            message: perms.role === 'owner' || perms.role === 'admin'
              ? 'Finish two-factor sign-in to view staff accounts.'
              : 'The staff list is available to Owner and Admin accounts. Your role can view licensing and customer data but not staff accounts.',
          }}
        />
      </div>
    );
  }

  return (
    <div className="page">
      <PageHeader
        title="Staff"
        subtitle="Manage admin dashboard access. Owner-only mutations. Disabling preserves history."
        icon={Users}
        accent="#a78bfa"
        actions={
          <>
            <Button variant="outline" size="sm" onClick={onRefresh}>
              <RefreshCw width={14} height={14}
                style={{ animation: refreshing ? 'spin 900ms linear infinite' : undefined }} />
              Refresh
            </Button>
            {canManage && (
              <Button variant="default" size="sm" onClick={() => setInviteOpen(true)}>
                <UserPlus width={14} height={14} />
                Invite staff
              </Button>
            )}
          </>
        }
      />

      <AccessNotice reason={canManage ? null : `${perms.ownerReason ?? ''} Inviting, changing roles and disabling accounts is not available to you.`} />

      {rows && <HeroPanel
        icon={Users}
        accent="#a78bfa"
        title="Access posture"
        subtitle="Active accounts, role distribution, and MFA enforcement."
        kpis={[
          <HeroKpi key="t" label="Live" value={stats.live} hint={`${stats.disabled} disabled`} />,
          <HeroKpi key="o" label="Owners"   value={stats.byRole.owner}   hint="root-of-trust" />,
          <HeroKpi key="a" label="Admins"   value={stats.byRole.admin} />,
          <HeroKpi key="s" label="Support"  value={stats.byRole.support} />,
          <HeroKpi key="m" label="MFA enrolled" value={stats.mfaPct === null ? '—' : `${stats.mfaPct}%`}
                   band={stats.mfaPct === null ? 'neutral'
                        : stats.mfaPct >= 100 ? 'healthy'
                        : stats.mfaPct >= 80 ? 'degraded'
                        : 'critical'}
                   hint={stats.mfaPct === null ? 'no live accounts' : `${stats.mfa}/${stats.live} accounts`} />,
        ]}
      />}

      <div style={{ marginTop: 16 }}>
        <MfaEnforcementNote />
      </div>

      <div
        style={{
          marginTop: 16,
          display: 'grid',
          gap: 12,
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 2fr)',
        }}
      >
        {rows ? <RoleAudit rows={rows} /> : <div />}
        <FetchState
          loading={!rows && !err}
          error={err}
          empty={!!rows && rows.length === 0}
          emptyTitle="No staff accounts"
          emptyDescription="Invite the first admin via the button up top."
          onRetry={onRefresh}
          skeleton="cards"
        >
          <RosterGrid
            rows={rows ?? []}
            currentUserId={state.user?.user_id ?? null}
            canManage={canManage}
            includeDisabled={includeDisabled}
            onIncludeDisabledToggle={setIncludeDisabled}
            onChangeRole={(id, role) => void onChangeRole(id, role)}
            onDisable={(id) => void onDisable(id)}
            onEnable={(id) => void onEnable(id)}
          />
        </FetchState>
      </div>

      <InviteDrawer
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onCreated={() => { setInviteOpen(false); onRefresh(); }}
        token={state.token}
      />
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * MFA enforcement note
 *
 * There is no staff-wide "enforce MFA" setting on the server. What the
 * license-server does enforce (internal.ts auth hook) is that Owner and
 * Admin sessions must have completed two-factor sign-in for every
 * /internal request. This panel states that, instead of showing a
 * switch that changes nothing.
 * ────────────────────────────────────────────────────────────────── */

function MfaEnforcementNote(): JSX.Element {
  return (
    <section
      style={{
        padding: 16,
        borderRadius: 14,
        border: '1px solid var(--border)',
        background: 'var(--bg-raised)',
        display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
      }}
    >
      <div
        aria-hidden="true"
        style={{
          flex: 'none',
          width: 40, height: 40,
          borderRadius: 10,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: '#34d3991f',
          color: '#34d399',
          border: '1px solid #34d39955',
        }}
      >
        <ScanLine width={18} height={18} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)' }}>
          Two-factor sign-in
        </div>
        <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
          Owner and Admin accounts must complete two-factor sign-in before any admin data is available.
          Support and Read-only accounts are not required to enroll. A switch to change this is not available yet.
        </p>
      </div>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Role audit panel
 * ────────────────────────────────────────────────────────────────── */

function RoleAudit({ rows }: { rows: StaffRow[] }): JSX.Element {
  const groups = useMemo(() => {
    const out: Array<{
      role: StaffRole;
      live: StaffRow[];
      mfa: number;
    }> = ROLES.map((role) => ({
      role,
      live: rows.filter((r) => r.role === role && !r.disabled_at),
      mfa: rows.filter((r) => r.role === role && !r.disabled_at && r.totp_enabled).length,
    }));
    return out;
  }, [rows]);

  return (
    <section
      style={{
        padding: 16,
        borderRadius: 14,
        border: '1px solid var(--border)',
        background: 'var(--bg-raised)',
        height: 'fit-content',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <Crown width={14} height={14} color="var(--text-muted)" />
        <h3
          style={{
            margin: 0,
            fontSize: '0.6875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: 'var(--text-muted)',
            fontWeight: 600,
          }}
        >
          Role audit
        </h3>
      </div>
      <ul
        style={{
          listStyle: 'none', padding: 0, margin: 0,
          display: 'flex', flexDirection: 'column', gap: 8,
        }}
      >
        {groups.map((g) => (
          <li
            key={g.role}
            style={{
              padding: '10px 12px',
              borderRadius: 10,
              background: 'var(--bg-overlay)',
              border: '1px solid var(--border)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <StatusChip band={bandForRole(g.role)} size="sm" noDot>
                {g.role}
              </StatusChip>
              <span style={{ marginLeft: 'auto', fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums', color: 'var(--text-muted)' }}>
                {g.live.length} live
              </span>
              <span
                style={{
                  fontSize: '0.6875rem', color: g.live.length > 0 && g.mfa < g.live.length ? 'var(--warning)' : 'var(--text-muted)',
                }}
              >
                MFA {g.mfa}/{g.live.length}
              </span>
            </div>
            {g.live.length === 0 ? (
              <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>(no accounts)</span>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {g.live.slice(0, 5).map((u) => (
                  <span
                    key={u.id}
                    style={{
                      fontSize: '0.6875rem',
                      color: 'var(--text-muted)',
                      display: 'flex',
                      gap: 4,
                      alignItems: 'center',
                    }}
                  >
                    <span style={{ color: 'var(--text-primary)' }}>{u.name}</span>
                    <span style={{ marginLeft: 'auto' }}>{u.email}</span>
                  </span>
                ))}
                {g.live.length > 5 && (
                  <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
                    +{g.live.length - 5} more
                  </span>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Roster grid
 * ────────────────────────────────────────────────────────────────── */

function RosterGrid({
  rows, currentUserId, canManage, includeDisabled, onIncludeDisabledToggle,
  onChangeRole, onDisable, onEnable,
}: {
  rows: StaffRow[];
  currentUserId: string | null;
  canManage: boolean;
  includeDisabled: boolean;
  onIncludeDisabledToggle: (v: boolean) => void;
  onChangeRole: (id: string, role: StaffRole) => void;
  onDisable: (id: string) => void;
  onEnable: (id: string) => void;
}): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <label
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '6px 10px', borderRadius: 8,
          border: '1px solid var(--border)', background: 'var(--bg-overlay)',
          fontSize: '0.75rem', color: 'var(--text-muted)',
          width: 'fit-content', cursor: 'pointer',
        }}
      >
        <input
          type="checkbox"
          checked={includeDisabled}
          onChange={(e) => onIncludeDisabledToggle(e.target.checked)}
        />
        Include disabled accounts
      </label>
      <div
        style={{
          display: 'grid',
          gap: 10,
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        }}
      >
        {rows.map((r) => (
          <StaffCard
            key={r.id}
            row={r}
            isMe={r.id === currentUserId}
            canManage={canManage}
            onChangeRole={(role) => onChangeRole(r.id, role)}
            onDisable={() => onDisable(r.id)}
            onEnable={() => onEnable(r.id)}
          />
        ))}
      </div>
    </div>
  );
}

function StaffCard({
  row, isMe, canManage, onChangeRole, onDisable, onEnable,
}: {
  row: StaffRow;
  isMe: boolean;
  canManage: boolean;
  onChangeRole: (role: StaffRole) => void;
  onDisable: () => void;
  onEnable: () => void;
}): JSX.Element {
  const disabled = !!row.disabled_at;
  return (
    <article
      style={{
        position: 'relative',
        padding: 14,
        borderRadius: 12,
        border: `1px solid ${disabled ? 'rgba(148,163,184,0.3)' : 'var(--border)'}`,
        background: 'var(--bg-raised)',
        opacity: disabled ? 0.6 : 1,
        display: 'flex', flexDirection: 'column', gap: 10,
      }}
    >
      <header style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Avatar email={row.email} name={row.name} role={row.role} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: '0.875rem', fontWeight: 600,
              color: 'var(--text-primary)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            {row.name}
            {isMe && (
              <span
                style={{
                  fontSize: '0.6875rem',
                  padding: '1px 6px',
                  borderRadius: 999,
                  background: 'var(--bg-overlay)',
                  border: '1px solid var(--border)',
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                }}
              >
                you
              </span>
            )}
          </div>
          <div
            style={{
              fontSize: '0.75rem', color: 'var(--text-muted)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}
          >
            {row.email}
          </div>
        </div>
      </header>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        {canManage && !isMe && !disabled ? (
          <select
            aria-label={`Role for ${row.email}`}
            value={row.role}
            onChange={(e) => onChangeRole(e.target.value as StaffRole)}
            style={{
              padding: '4px 8px',
              borderRadius: 999,
              border: `1px solid ${ROLE_TONES[row.role]}55`,
              background: `${ROLE_TONES[row.role]}1f`,
              color: ROLE_TONES[row.role],
              fontSize: '0.6875rem',
              fontWeight: 500,
              outline: 'none',
            }}
          >
            {ROLES.map((role) => (
              <option key={role} value={role}>{role}</option>
            ))}
          </select>
        ) : (
          <StatusChip band={bandForRole(row.role)} size="sm" noDot>
            {row.role}
          </StatusChip>
        )}
        {row.totp_enabled ? (
          <StatusChip band="healthy" size="sm">
            <ShieldCheck width={10} height={10} style={{ display: 'inline', marginRight: 2 }} />
            MFA on
          </StatusChip>
        ) : (
          <StatusChip band="critical" size="sm">
            <ShieldAlert width={10} height={10} style={{ display: 'inline', marginRight: 2 }} />
            MFA off
          </StatusChip>
        )}
        <StatusChip
          band={disabled ? 'critical' : 'neutral'}
          size="sm"
          noDot
        >
          {disabled ? 'Disabled' : 'Active'}
        </StatusChip>
      </div>

      <div
        style={{
          display: 'flex', justifyContent: 'space-between',
          fontSize: '0.6875rem', color: 'var(--text-muted)',
          paddingTop: 6, borderTop: '1px solid var(--border)',
        }}
      >
        <span>SSO via {row.sso_provider}</span>
        <span>
          {row.last_login_at
            ? `last login ${fmtRelative(row.last_login_at)}`
            : 'never logged in'}
        </span>
      </div>

      {canManage && !isMe && (
        <div style={{ display: 'flex', gap: 6 }}>
          {disabled ? (
            <Button variant="outline" size="sm" onClick={onEnable} style={{ flex: 1 }}>
              <Power width={12} height={12} />
              Enable
            </Button>
          ) : (
            <Button variant="ghost" size="sm" onClick={onDisable} style={{ flex: 1 }}>
              <PowerOff width={12} height={12} />
              Disable
            </Button>
          )}
        </div>
      )}
    </article>
  );
}

function Avatar({
  email, name, role,
}: { email: string; name: string; role: StaffRole }): JSX.Element {
  const initials = (name || email)
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('')
    || '?';
  let hash = 0;
  for (let i = 0; i < email.length; i++) hash = (hash * 31 + email.charCodeAt(i)) | 0;
  const h = Math.abs(hash) % 360;
  return (
    <div
      aria-hidden
      style={{
        flex: 'none',
        width: 40, height: 40,
        borderRadius: 12,
        background: `linear-gradient(135deg, hsl(${h} 70% 55%), hsl(${(h + 60) % 360} 70% 45%))`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '0.875rem', fontWeight: 600,
        color: '#0b0f19',
        border: `2px solid ${ROLE_TONES[role]}55`,
        position: 'relative',
      }}
    >
      {initials}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Invite drawer
 * ────────────────────────────────────────────────────────────────── */

function InviteDrawer({
  open, onClose, onCreated, token,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  token: string | null;
}): JSX.Element {
  const [form, setForm] = useState({
    email: '',
    name: '',
    sso_provider: 'google' as 'google' | 'okta',
    sso_subject_id: '',
    role: 'support' as StaffRole,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm({
        email: '', name: '',
        sso_provider: 'google',
        sso_subject_id: '',
        role: 'support',
      });
      setErr(null);
    }
  }, [open]);

  const submit = async (): Promise<void> => {
    setBusy(true); setErr(null);
    try {
      const api = new AdminApiClient({ baseUrl: '', getToken: () => token });
      await api.createStaff(form);
      onCreated();
    } catch (e) {
      setErr(userMessageFor(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width="md"
      title="Invite staff"
      subtitle="Staff sign in through Google or Okta single sign-on."
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button
            variant="default"
            size="sm"
            onClick={() => void submit()}
            disabled={busy || !form.email || !form.name || !form.sso_subject_id}
          >
            <UserPlus width={14} height={14} />
            {busy ? 'Inviting…' : 'Send invite'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {err && <p className="err" role="alert" style={{ fontSize: '0.8125rem' }}>{err}</p>}

        <Field label="Email">
          <input
            type="email"
            aria-label="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="alex@yourcompany.com"
            style={inputStyle}
          />
        </Field>
        <Field label="Name">
          <input
            aria-label="Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Alex Doe"
            style={inputStyle}
          />
        </Field>
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: '1fr 2fr' }}>
          <Field label="SSO provider">
            <select
              aria-label="SSO provider"
              value={form.sso_provider}
              onChange={(e) => setForm({ ...form, sso_provider: e.target.value as 'google' | 'okta' })}
              style={inputStyle}
            >
              <option value="google">google</option>
              <option value="okta">okta</option>
            </select>
          </Field>
          <Field label="SSO subject id" hint="Stable provider id (e.g. Google sub claim).">
            <input
              aria-label="SSO subject id"
              value={form.sso_subject_id}
              onChange={(e) => setForm({ ...form, sso_subject_id: e.target.value })}
              placeholder="116…"
              style={inputStyle}
            />
          </Field>
        </div>
        <Field label="Role">
          <div
            style={{
              display: 'flex',
              gap: 6,
              padding: 4,
              borderRadius: 10,
              border: '1px solid var(--border)',
              background: 'var(--bg-overlay)',
              flexWrap: 'wrap',
            }}
          >
            {ROLES.map((r) => {
              const active = form.role === r;
              return (
                <button
                  key={r}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setForm({ ...form, role: r })}
                  style={{
                    flex: 1,
                    padding: '6px 10px',
                    border: 'none',
                    borderRadius: 7,
                    background: active ? `${ROLE_TONES[r]}1f` : 'transparent',
                    color: active ? ROLE_TONES[r] : 'var(--text-muted)',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                  }}
                >
                  {r}
                </button>
              );
            })}
          </div>
        </Field>

        <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-muted)' }}>
          Owner and Admin accounts must enroll two-factor sign-in before they can use the dashboard.
        </p>
      </div>
    </Drawer>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Helpers
 * ────────────────────────────────────────────────────────────────── */

function Field({
  label, hint, children,
}: { label: string; hint?: string; children: React.ReactNode }): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{label}</span>
      {children}
      {hint && <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>{hint}</span>}
    </div>
  );
}

const inputStyle = {
  width: '100%',
  padding: 8,
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--bg-overlay)',
  color: 'var(--text-primary)',
  fontSize: '0.8125rem',
  outline: 'none',
  fontFamily: 'inherit',
} as const;

function fmtRelative(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

